import { ImageScaleView, QuickViewPool, View } from '@play-co/timestep-core/ui';
import ResponsiveScene from './ResponsiveScene';
import { Container } from '../minigame/match2-timestep/odie';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import sounds from 'src/lib/sounds';
import { GameEvent } from '../minigame/match2-timestep/game/defs/event';
import { CustomGameScene } from '../minigame/match2-timestep/game/CustomGameScene';
import { downscale } from 'src/conf/scale';
import { AssetGroup } from '@play-co/timestep-core';
import { getPuzzleAssets } from 'src/loadMatch2LevelAssets';
import { RoundTimer } from '../rescueLevel/components/RoundTimer';
import { now } from 'src/StateObserver';
import { GoalProgress } from '../rescueLevel/components/GoalProgress';
import {
  RescueLevelHeader,
  getRescueLevelTheme,
} from '../rescueLevel/rescueLevel.theme';
import { Instruction } from '../rescueLevel/components/Instruction';
import { TimesUp } from '../rescueLevel/components/TimesUp';
import { applyRelativeOffset, waitForItPromise } from 'src/lib/utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import type { BlockEntity } from '../minigame/match2-timestep/game/entities/BlockEntity';
import gsap from 'gsap';
import { throttleLeading } from '../animation/animation.util';
import NakedPromise from '../minigame/match2-timestep/lib/pattern/NakedPromise';
import buttons from 'src/lib/ui/config/buttons';

export type RescuePuzzleFinishArgs = {
  remainingTimeMs: number;
  timeoutMs: number;
};

export default class RescueLevelScene extends ResponsiveScene {
  private gameStage?: Container;
  private game?: CustomGameScene;
  private quit: ButtonScaleView;
  private gameContainer: View;
  private header: RescueLevelHeader;
  private bricks: ImageScaleView;
  private info: ImageScaleView;
  private timer: RoundTimer;
  private goalProgress: GoalProgress;
  private lastTime = 0;
  private remainingMs = 0;
  private playing = false;
  private timeout = 0;
  private levelData = '';
  private instruction: Instruction;
  private timesUp: TimesUp;
  private onPuzzleStart?: () => void;
  private onPuzzleFinish?: (args: RescuePuzzleFinishArgs) => void;
  private iconPool: QuickViewPool<View>;
  private roundPromise?: NakedPromise<void>;

  // Throttled sound effect functions to prevent spamming
  private playGoalClearSfx?: () => void;
  private playGoalCollectSfx?: () => void;

  constructor() {
    super(
      new View({
        backgroundColor: '#A0D80A',
      }),
    );

    const view = this.getView();

    this.header = getRescueLevelTheme().getHeader({
      superview: view,
    });

    this.bricks = new ImageScaleView({
      parent: view,
      image: 'assets/minigame/rescueLevel/bricks.png',
      scaleMethod: 'cover',
      zIndex: -2,
    });

    this.info = new ImageScaleView({
      parent: view,
      image: 'assets/minigame/rescueLevel/info.png',
      scaleMethod: 'cover',
      width: downscale(1080),
      height: downscale(221),
    });

    this.timer = new RoundTimer({
      superview: this.info,
      x: 100,
      y: this.info.style.height / 2,
    });

    this.goalProgress = new GoalProgress({
      superview: this.info,
      x: this.info.style.width - 350,
      y: 35,
    });

    // Set up a container for the game - this container will be moved with the resize.
    this.gameContainer = new View({
      superview: view,
      x: this.getView().style.width / 2,
      y: 400,
      zIndex: -1,
      infinite: true,
    });

    this.quit = new ButtonScaleView({
      ...buttons.close,
      parent: view,
      x: 400,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.QUIT.BUTTON,
      },
      onClick: async () => {
        sounds.playSound('ok');
        this.puzzleQuit();
      },
      visible: false,
    });

    this.instruction = new Instruction({
      superview: view,
      width: view.style.width,
      height: view.style.height,
      x: view.style.width / 2,
      y: view.style.height / 2,
      centerOnOrigin: true,
    });
    this.instruction.onInputStart = this.puzzleStart.bind(this);

    this.timesUp = new TimesUp({
      superview: view,
      visible: false,
    });

    this.iconPool = new QuickViewPool(() => {
      return getRescueLevelTheme().getIconSmall({
        superview: this.getView(),
        centerOnOrigin: true,
      });
    });
  }

  public setup(args: {
    levelData: string;
    timeout: number;
    onPuzzleStart?: () => void;
    onPuzzleFinish?: (args: RescuePuzzleFinishArgs) => void;
  }) {
    this.levelData = args.levelData;
    this.timeout = args.timeout;
    this.onPuzzleStart = args.onPuzzleStart;
    this.onPuzzleFinish = args.onPuzzleFinish;
    this.timer.setTime(this.timeout, this.timeout);
  }

  public showScene() {
    if (process.env.IS_DEVELOPMENT) {
      this.getView().on('RescueLevelAutoWin', () => {
        this.game?.cheatSystem?.winGame();
      });
      this.getView().on('RescueLevel12Secs', () => {
        this.remainingMs = 12000;
      });
      this.getView().on('RescueLevel3Secs', () => {
        this.remainingMs = 3000;
      });
    }

    if (!this.gameStage) {
      this.gameStage = new Container();
      this.gameStage.tag = 'GameStage';
      this.gameContainer.addSubview(this.gameStage.view);
    }

    this.game = new CustomGameScene({
      stage: this.gameStage!,
      levelData: this.levelData,
      infiniteMoves: true,
      onObstacleCleared: this.onObstacleCleared.bind(this),
    });
    const { width, height } = this.getView().style;
    this.game.resize(width, height);
    this.game.startGame();
    this.game.events.subscribe(this.handleGameEvents.bind(this));
    this.updateGoal(false);

    // Hold the game until puzze start
    this.gameContainer.updateOpts({
      blockEvents: true,
    });

    // Play rescue level music
    sounds.music.play('rescue');
  }

  public hideScene() {
    if (process.env.IS_DEVELOPMENT) {
      this.getView().removeAllListeners('RescueLevelAutoWin');
      this.getView().removeAllListeners('RescueLevel12Secs');
      this.getView().removeAllListeners('RescueLevel3Secs');
    }

    this.puzzleEnd();
    // Play regular music
    sounds.music.play('bgm');

    // Fully destroy the game to free up memory
    this.game?.destroy();

    // Ensure that game and game stage are eligible for garbage collection
    this.game = undefined;
    this.gameStage = undefined;
  }

  private handleGameEvents(e?: GameEvent) {
    if (!e || !this.game) return;

    if (e.id === 'finish') {
      // Fully block user input when game is finished
      void this.puzzleEnd();
    }

    if (e.id === 'input') {
      // Create a new round promise on user input
      this.roundPromise = new NakedPromise<void>();
    }

    if (e.id === 'round') {
      // Resolve the promise when the game settles
      this.roundPromise?.resolve();
    }
  }

  public override resize() {
    super.resize();

    this.header.updateOpts({
      x: 0,
      y: 0,
    });

    this.info.updateOpts({
      y: this.header.style.height,
    });

    this.bricks.updateOpts({
      width: this.getView().style.width,
      height:
        this.getView().style.height -
        this.header.style.height -
        this.info.style.height,
      y: this.header.style.height + this.info.style.height,
    });

    this.quit.updateOpts({
      x: this.getView().style.width - buttons.close.width * 0.5 - 16,
      y: buttons.close.height * 0.5 + 16,
    });

    this.gameContainer.updateOpts({
      x: this.getView().style.width / 2,
      y: this.getView().style.height * 0.685,
    });

    this.instruction.updateOpts({
      x: this.getView().style.width / 2,
      y: this.getView().style.height / 2,
      width: this.getView().style.width,
      height: this.getView().style.height,
      centerOnOrigin: true,
    });

    this.timesUp.updateOpts({
      x: this.bricks.style.x,
      y: this.bricks.style.y,
      width: this.bricks.style.width,
      height: this.bricks.style.height,
    });
  }

  public async preload() {
    // Only preload if level data is set
    if (!this.levelData) return;

    const { blockAssetPaths, blockAssetDescriptions } = getPuzzleAssets(
      this.levelData,
    );

    const assetsGroup = new AssetGroup([
      ...AssetGroup.constructURLs([
        'assets/particles/',
        'assets/minigame/rescueLevel/',
        `assets/sounds/music/Rescue_BGM.mp3`,
        ...blockAssetPaths,
      ]),
      ...blockAssetDescriptions,
    ]);

    await assetsGroup.load();
  }

  private puzzleStart() {
    sounds.playSound('milestone');
    void this.instruction.hideAnimated();
    this.gameContainer.updateOpts({
      blockEvents: false,
    });
    this.playing = true;
    this.remainingMs = this.timeout;
    this.lastTime = now();
    // Start any header motion that should stay synchronized with the puzzle timer.
    this.header.playPuzzleStart?.(this.timeout - 10000);
    this.updateTime();
    this.updateGoal(false);
    this.getView().setTick(this.updateTime.bind(this));
    this.onPuzzleStart?.();
    this.quit.show();
  }

  private async puzzleEnd() {
    if (!this.playing) return;
    this.playing = false;
    this.quit.hide();
    this.getView().updateOpts({
      blockEvents: true,
    });
    this.gameContainer.updateOpts({
      blockEvents: true,
    });

    // Wait the game settle before actually finishing
    await this.roundPromise;

    this.onPuzzleFinish?.({
      remainingTimeMs: this.remainingMs,
      timeoutMs: this.timeout,
    });

    const result = this.game?.getResult();
    if (result === 'win') {
      await this.playWinAnimation();
    } else {
      await this.playLoseAnimation();
    }
    this.game?.resolveCompletePromise();
  }

  private async puzzleQuit() {
    if (!this.playing) return;
    this.playing = false;
    this.onPuzzleFinish?.({
      remainingTimeMs: this.remainingMs,
      timeoutMs: this.timeout,
    });
    this.quit.hide();
    this.getView().updateOpts({
      blockEvents: true,
    });
    this.gameContainer.updateOpts({
      blockEvents: true,
    });
    await this.playQuitAnimation();
    this.game?.resolveCompletePromise();
  }

  private updateTime() {
    if (!this.playing) return;
    const time = now();
    this.remainingMs -= time - this.lastTime;
    this.lastTime = time;
    this.timer.setTime(this.remainingMs, this.timeout);

    if (this.remainingMs <= 10000 && !this.header.tenSecsLeft) {
      void this.header.playTenSecsLeft();
    }

    if (this.remainingMs <= 0) {
      this.remainingMs = 0;
      // On timeout, the game should be marked as failed
      this.game?.fail();
      this.puzzleEnd();
    }
  }

  private updateGoal(animated: boolean) {
    if (!this.game) return;
    const total = this.game.getTotalObstacleGoals();
    const value = total - this.game.getRemainingGoals();
    if (!animated) {
      this.goalProgress.setProgress(value, total);
    } else {
      this.goalProgress.animateProgress(value, total);
    }
  }

  private async playWinAnimation() {
    sounds.playSound('win');
    await this.header.playRescue();
    this.header.pet.hide();
    await openPopupPromise('popupRescueLevelWin', {});
  }

  private async playLoseAnimation() {
    sounds.playSound('puzzle-lose-new');
    await this.timesUp.showAnimated();
    // Give user some time to process the information
    await waitForItPromise(800);
    await this.header.playFail();
  }

  private async playQuitAnimation() {
    sounds.playSound('puzzle-lose-new');
    await this.header.playQuit();
  }

  private async onObstacleCleared(entity: BlockEntity) {
    if (!this.game) return;

    if (!this.playGoalClearSfx) {
      this.playGoalClearSfx = throttleLeading(() => {
        sounds.playSound('gemsCollected');
      }, 50);
    }
    this.playGoalClearSfx();

    const entityView = entity.c.view2d.view.view;
    const fromPos = entityView.getPosition();
    applyRelativeOffset(fromPos, this.game.stage.view, this.getView());

    const toPos = this.goalProgress.icon.getPosition(this.goalProgress);
    applyRelativeOffset(toPos, this.goalProgress, this.getView());

    const icon = this.iconPool.obtainView();
    gsap.killTweensOf(icon.style);
    icon.updateOpts({
      superview: this.getView(),
      x: fromPos.x + 38,
      y: fromPos.y + 38,
      scale: 1,
    });

    const scaleUp = { scale: 2 };
    const scaleToGoal = { scale: 1 };
    const duration = 0.6;

    void gsap.to(icon.style, {
      ...scaleUp,
      duration: duration * 0.3,
      ease: 'sine.out',
    });

    void gsap.to(icon.style, {
      ...scaleToGoal,
      duration: duration * 0.7,
      ease: 'sine.in',
      delay: duration * 0.3,
    });

    void gsap.to(icon.style, {
      x: toPos.x + icon.style.width / 2,
      duration,
      ease: 'quad.inOut',
    });

    await gsap.to(icon.style, {
      y: toPos.y + icon.style.height / 2,
      duration,
      ease: 'sine.inOut',
    });

    icon.removeFromSuperview();
    this.iconPool.releaseView(icon);
    this.updateGoal(true);

    if (!this.playGoalCollectSfx) {
      this.playGoalCollectSfx = throttleLeading(() => {
        sounds.playSound('sfx-star1', 0.2);
      }, 50);
    }
    this.playGoalCollectSfx();
  }
}
